import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import {
  AlertCircle,
  X,
  Package,
  Clock,
  ExternalLink,
  MessageSquare,
  Share2,
  Loader2,
  Barcode as BarcodeIcon,
  ChevronDown,
} from "lucide-react";
import type { StoreProductResult } from "../services/ProductSearchService";
import type { HomeDepotStoreProductResult } from "../services/ProductSearchService";
import { useUserContext } from "../context/UserContext";
import Barcode from "react-barcode";
import DiscordPriceModal from "./DiscordPriceModal";
import { createDeal, getDealShareUrl } from "../services/DealsService";
import { toast } from "react-toastify";
import { useTranslation } from "../context/LanguageContext";
import {
  formatPrice,
  getFirstStoreSearchError,
  getProductInfo,
  getWalmartBarcode,
} from "./NearbyResults.utils";
import { buildRetailerLookupUrl, getRetailerLinkLabel } from "../utils/productUrls";
import { useEnrichedSearchResults } from "../hooks/useEnrichedSearchResults";
import NearbyStoreResultsList from "./NearbyStoreResultsList";
import type { StoreSearchSort } from "../utils/storeSearchSort";
import { getProductImageUrl } from "../utils/imageUtils";

interface AmazonDataProps {
  price?: number;
  title?: string;
  url?: string;
  isPrime?: boolean;
}

interface NearbyResultsProps {
  // Search state
  isSearching: boolean;
  searchError: string | null;
  onClose: () => void;

  // Results data
  searchResults?: StoreProductResult[];
  homeDepotSearchResults?: HomeDepotStoreProductResult[];

  // Retailer context
  isHomeDepot?: boolean;
  retailer?: string;

  // Optional title override
  title?: string;

  // Amazon pricing
  amazonData?: AmazonDataProps;

  // Zip code used for the search (falls back to user zip)
  searchZipCode?: string;
}

const NearbyResults: React.FC<NearbyResultsProps> = ({
  isSearching,
  searchError,
  onClose,
  searchResults,
  homeDepotSearchResults,
  isHomeDepot = false,
  retailer = "walmart",
  title,
  amazonData,
  searchZipCode,
}) => {
  const { user } = useUserContext();
  const zipForDistance = searchZipCode?.trim() || user?.zipCode?.trim() || "";
  const enrichedSearchResults = useEnrichedSearchResults(searchResults, zipForDistance, retailer);
  const enrichedHomeDepotResults = useEnrichedSearchResults(
    homeDepotSearchResults,
    zipForDistance,
    "home_depot"
  );
  const [showDiscordModal, setShowDiscordModal] = useState(false);
  const [shareLoading, setShareLoading] = useState(false);
  const [showImageViewer, setShowImageViewer] = useState(false);
  const [showBarcode, setShowBarcode] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [storeSort, setStoreSort] = useState<StoreSearchSort>("best_deal");
  const { t } = useTranslation();

  useEffect(() => {
    setStoreSort("best_deal");
    setShowBarcode(false);
  }, [searchResults, homeDepotSearchResults]);

  useEffect(() => {
    const frame = requestAnimationFrame(() => setIsVisible(true));
    return () => cancelAnimationFrame(frame);
  }, []);

  const discordProduct = enrichedSearchResults?.find((r) => !r.error) ?? null;

  const handleShareFromNearby = async () => {
    if (shareLoading) return;
    const info = getProductInfo({ isHomeDepot, searchResults: enrichedSearchResults, homeDepotSearchResults: enrichedHomeDepotResults, retailer });
    const firstResult = enrichedSearchResults?.find((r) => !r.error) || enrichedHomeDepotResults?.find((r) => !r.error);
    if (!info || !firstResult) return;
    const onlinePrice = "onlinePrice" in firstResult ? firstResult.onlinePrice : 0;
    const category = "categoryPath" in firstResult
      ? firstResult.categoryPath || firstResult.mainCategory
      : "";
    setShareLoading(true);
    try {
      const dealId = await createDeal({
        upc: info.sku || "", sku: info.sku || "", name: info.name || "",
        msrp: firstResult.msrp || 0, onlinePrice: onlinePrice || 0,
        storePrice: firstResult.storePrice || 0, image: info.image || "",
        storeId: firstResult.storeId || "", retailer: retailer,
        category: category || "",
        url: "", aisle: firstResult.aisles || "",
        inStock: firstResult.inventory > 0,
        discountPercentage: firstResult.msrp > 0 && firstResult.storePrice < firstResult.msrp
          ? Math.round(((firstResult.msrp - firstResult.storePrice) / firstResult.msrp) * 100) : 0,
      });
      const shareUrl = getDealShareUrl(dealId);
      await navigator.clipboard.writeText(shareUrl);
      toast.success(t("card.shareLinkCopied"));
    } catch {
      toast.error(t("card.shareLinkFailed"));
    } finally {
      setShareLoading(false);
    }
  };

  const loadingTips = [
    t("products.nearbyOnHandTip"),
    t("products.nearbyLowOnHandNoAisleTip"),
  ];

  // // Handle escape key to close modal
  // useEffect(() => {
  //   const handleEscape = (e: KeyboardEvent) => {
  //     if (e.key === "Escape") {
  //       onClose();
  //     }
  //   };

  //   document.addEventListener("keydown", handleEscape);
  //   return () => document.removeEventListener("keydown", handleEscape);
  // }, [onClose]);

  return createPortal(
    <div className="fixed inset-0 flex items-end sm:items-center sm:justify-center" style={{ zIndex: 99999 }}>
      {/* Backdrop */}
      <div
        className={`absolute inset-0 bg-black/20 dark:bg-black/60 backdrop-blur-sm transition-opacity duration-200 ${
          isVisible ? "opacity-100" : "opacity-0"
        }`}
        onClick={!isSearching ? onClose : undefined}
      />

      {/* Bottom Sheet / Modal */}
      <div
        className={`relative w-full sm:max-w-xl lg:max-w-2xl max-h-[80vh] sm:max-h-[90vh] bg-white dark:bg-dark-800 rounded-t-2xl sm:rounded-2xl shadow-2xl border border-gray-100 dark:border-dark-600 overflow-hidden sm:mx-4 flex flex-col transition-all duration-300 ease-out sm:duration-200 ${
          isVisible
            ? "translate-y-0 opacity-100 sm:scale-100"
            : "translate-y-full sm:opacity-0 sm:translate-y-6 sm:scale-[0.98]"
        }`}
      >
        {/* Drag Handle (mobile only) */}
        <div className="flex justify-center pt-2 sm:hidden">
          <div className="w-10 h-1 rounded-full bg-gray-300 dark:bg-dark-600" />
        </div>

        {/* Compact Header + Product Info */}
        <div className="flex-shrink-0">
          {isSearching ? (
            <div className="flex items-center justify-end px-4 sm:px-6 pt-3 sm:pt-4 pb-3">
              <button
                onClick={onClose}
                className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-500 hover:text-gray-700 dark:text-dark-300 dark:hover:text-dark-100 transition-colors"
              >
                <X className="w-4 h-4" />
                {t("common.close")}
              </button>
            </div>
          ) : (
            <div className="px-4 sm:px-5 pt-3 pb-2.5">
              <div className="flex items-start gap-2.5">
                {/* Product Image */}
                {(() => {
                  const productInfo = getProductInfo({ isHomeDepot, searchResults: enrichedSearchResults, homeDepotSearchResults: enrichedHomeDepotResults, retailer });
                  return productInfo?.image ? (
                    <button onClick={() => setShowImageViewer(true)} className="flex-shrink-0 rounded-lg focus-visible:outline-2 focus-visible:outline-blue-500">
                      <img src={getProductImageUrl(productInfo.image, "400")} alt={productInfo.name} className="w-11 h-11 object-contain rounded-lg bg-white hover:opacity-80 transition-opacity cursor-zoom-in" />
                    </button>
                  ) : (
                    <div className="w-11 h-11 bg-gray-100 dark:bg-dark-700 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Package className="w-5 h-5 text-gray-400 dark:text-dark-400" />
                    </div>
                  );
                })()}

                {/* Product Name + SKU */}
                <div className="flex-1 min-w-0">
                  {(() => {
                    const productInfo = getProductInfo({ isHomeDepot, searchResults: enrichedSearchResults, homeDepotSearchResults: enrichedHomeDepotResults, retailer });
                    if (!productInfo) return <h3 className="text-sm font-bold text-gray-900 dark:text-white">{title || t("products.storeAvailability")}</h3>;
                    return (
                      <>
                        <h3 className="text-sm font-semibold text-gray-900 dark:text-white line-clamp-2 leading-5" title={productInfo.name}>
                          {productInfo.name}
                        </h3>
                        <div className="mt-1 flex items-center gap-x-2 gap-y-0.5 flex-wrap">
                          {productInfo.sku && (
                            <span className="text-[10px] leading-4 text-gray-500 dark:text-dark-200 whitespace-nowrap">
                              {retailer === "target" ? t("card.tcin") : t("card.sku")}: {productInfo.sku}
                            </span>
                          )}
                          {productInfo.barcode && productInfo.barcode !== productInfo.sku && (
                            <span className="text-[10px] leading-4 text-gray-500 dark:text-dark-200 whitespace-nowrap">
                              {t("card.upc")}: {productInfo.barcode}
                            </span>
                          )}
                        </div>
                        <div className="mt-1 flex items-center gap-1.5 flex-wrap">
                          {amazonData?.price != null && amazonData.price > 0 && (amazonData.url ? (
                            <a
                              href={amazonData.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 px-2 py-0.5 bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-300 rounded text-[11px] font-semibold hover:bg-orange-200 dark:hover:bg-orange-900/60 transition-colors"
                            >
                              {t("card.amazonPrice", { price: `$${amazonData.price.toFixed(2)}` })}
                              {amazonData.isPrime && <span className="text-[9px] font-bold text-blue-600 dark:text-blue-400">{t("card.prime")}</span>}
                              <ExternalLink className="w-2.5 h-2.5" />
                            </a>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-300 rounded text-[11px] font-semibold">
                              {t("card.amazonPrice", { price: `$${amazonData.price.toFixed(2)}` })}
                              {amazonData.isPrime && <span className="text-[9px] font-bold text-blue-600 dark:text-blue-400">{t("card.prime")}</span>}
                            </span>
                          ))}
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-gray-100 dark:bg-dark-700 text-gray-700 dark:text-dark-200 rounded text-[11px] font-semibold">
                            {t("card.msrp")} {productInfo.msrp != null ? formatPrice(productInfo.msrp) : "—"}
                          </span>
                        </div>
                      </>
                    );
                  })()}
                </div>

                {/* Close */}
                <button
                  onClick={onClose}
                  aria-label={t("common.close")}
                  className="inline-flex h-8 w-8 items-center justify-center rounded-full text-gray-500 hover:bg-gray-100 hover:text-gray-700 dark:text-dark-300 dark:hover:bg-dark-700 dark:hover:text-dark-100 transition-colors flex-shrink-0"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
          )}

          {/* Actions + Barcode */}
          {!isSearching && (() => {
            const productInfo = getProductInfo({ isHomeDepot, searchResults: enrichedSearchResults, homeDepotSearchResults: enrichedHomeDepotResults, retailer });
            if (!productInfo) return null;
            const walmartBarcode = getWalmartBarcode({ retailer, searchResults: enrichedSearchResults });
            const displayBarcode = walmartBarcode || productInfo.barcode || null;
            const firstResult = enrichedSearchResults?.find((r) => !r.error);
            const id = firstResult?.sku || firstResult?.barcode || productInfo.sku || "";
            const labelMap: Record<string, string> = {
              home_depot: "HD.com",
              target: "Target.com",
              target_online: "Target.com",
              dollar_general: "DollarGeneral.com",
              bestbuy: "BestBuy.com",
              costco: "Costco.com",
              amazon: "Amazon.com",
              lowes: "Lowes.com",
              kohls: "Kohls.com",
              woot: "Woot.com",
              newegg: "Newegg.com",
              gamestop: "GameStop.com",
              bjs: "BJs.com",
              meijer: "Meijer.com",
              menards: "Menards.com",
              academy: "Academy.com",
              walgreens: "Walgreens.com",
              zappos: "Zappos.com",
              jcpenney: "JCPenney.com",
              belk: "Belk.com",
              michaels: "Michaels.com",
              nike: "Nike.com",
              adidas: "Adidas.com",
              reebok: "Reebok.com",
              skechers: "Skechers.com",
              puma: "Puma.com",
              underarmour: "UnderArmour.com",
              columbia: "Columbia.com",
              nordstromrack: "NordstromRack.com",
              aldo: "AldoShoes.com",
              vitacost: "Vitacost.com",
              journeys: "Journeys.com",
              shopdisney: "DisneyStore.com",
              ninjakitchen: "NinjaKitchen.com",
              hottopic: "HotTopic.com",
              officedepot: "OfficeDepot.com",
              barnesnoble: "BarnesAndNoble.com",
              razer: "Razer.com",
              pacsun: "PacSun.com",
              bathbodyworks: "BathAndBodyWorks.com",
              staples: "Staples.com",
              petsmart: "PetSmart.com",
              ulta: "Ulta.com",
              forever21: "Forever21.com",
              gap: "Gap.com",
              petco: "Petco.com",
              americaneagle: "AE.com",
              aerie: "AE.com",
              carters: "Carters.com",
              abercrombie: "Abercrombie.com",
              hollister: "HollisterCo.com",
              oldnavy: "OldNavy.com",
              bananarepublic: "BananaRepublic.com",
              stevemadden: "SteveMadden.com",
              athleta: "Athleta.com",
              gapfactory: "GapFactory.com",
              bananarepublicfactory: "BananaRepublicFactory.com",
              boxlunch: "BoxLunch.com",
              verabradley: "VeraBradley.com",
              footlocker: "FootLocker.com",
              champs: "ChampsSports.com",
            };
            const url = buildRetailerLookupUrl(retailer, id);
            const label = retailer === "walmart" ? "Walmart" : labelMap[retailer] || getRetailerLinkLabel(retailer);

            return (
              <div className="px-4 sm:px-5 pb-3 space-y-2">
                {/* Chip row */}
                <div className="flex items-center flex-wrap gap-1.5">
                  {id && (
                    <a
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex min-h-8 items-center gap-1 px-2.5 rounded-lg text-[11px] font-semibold bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-900/50 transition-colors"
                    >
                      <ExternalLink className="w-3 h-3" />
                      {label}
                    </a>
                  )}
                  <button
                    onClick={handleShareFromNearby}
                    disabled={shareLoading}
                    className="inline-flex min-h-8 items-center gap-1 px-2 rounded-lg text-[11px] font-medium bg-gray-50 dark:bg-dark-700 text-gray-600 dark:text-dark-200 hover:bg-gray-100 dark:hover:bg-dark-600 transition-colors disabled:opacity-60"
                  >
                    {shareLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Share2 className="w-3 h-3" />}
                    {t("card.share")}
                  </button>
                  {retailer === "walmart" && (user?.admin || user?.allowSendToDiscord) && discordProduct && (
                    <button
                      onClick={() => setShowDiscordModal(true)}
                      className="inline-flex min-h-8 items-center gap-1 px-2 rounded-lg text-[11px] font-medium bg-gray-50 dark:bg-dark-700 text-gray-600 dark:text-dark-200 hover:bg-gray-100 dark:hover:bg-dark-600 transition-colors"
                    >
                      <MessageSquare className="w-3 h-3" />
                      {t("card.discord")}
                    </button>
                  )}
                  {displayBarcode && (
                    <button
                      type="button"
                      onClick={() => setShowBarcode((shown) => !shown)}
                      aria-label={t(showBarcode ? "products.hideBarcode" : "products.showBarcode")}
                      aria-expanded={showBarcode}
                      aria-controls="nearby-product-barcode"
                      className="inline-flex min-h-8 items-center gap-1 px-2 rounded-lg text-[11px] font-medium bg-gray-50 dark:bg-dark-700 text-gray-600 dark:text-dark-200 hover:bg-gray-100 dark:hover:bg-dark-600 transition-colors"
                    >
                      <BarcodeIcon className="w-3.5 h-3.5" />
                      {t("products.barcode")}
                      <ChevronDown className={`w-3 h-3 transition-transform ${showBarcode ? "rotate-180" : ""}`} />
                    </button>
                  )}
                </div>
                {displayBarcode && showBarcode && (
                  <div id="nearby-product-barcode" className="flex justify-center overflow-x-auto bg-white rounded-lg border border-gray-100 p-3">
                    <Barcode value={displayBarcode} width={1.5} height={44} fontSize={11} margin={4} />
                  </div>
                )}
              </div>
            );
          })()}
        </div>

        {/* Search Error */}
        {searchError && (
          <div className={`mx-3 sm:mx-6 mt-3 p-3 rounded-xl border flex-shrink-0 ${
            searchError.toLowerCase().includes("maintenance")
              ? "bg-amber-50 border-amber-200 dark:bg-amber-900/20 dark:border-amber-800"
              : "bg-red-50 border-red-200 dark:bg-red-900/20 dark:border-red-800"
          }`}>
            <div className="flex items-start gap-2">
              {searchError.toLowerCase().includes("maintenance") ? (
                <Clock className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
              ) : (
                <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
              )}
              <p className={`text-xs leading-relaxed ${
                searchError.toLowerCase().includes("maintenance") ? "text-amber-800 dark:text-amber-300" : "text-red-800 dark:text-red-300"
              }`}>
                {searchError}
              </p>
            </div>
          </div>
        )}

        {/* Store Results */}
        {(() => {
          const raw = isHomeDepot
            ? (enrichedSearchResults?.length ? enrichedSearchResults : enrichedHomeDepotResults || [])
            : (enrichedSearchResults || []);
          const filtered = raw.filter((result) => !result.error);
          const resultError = getFirstStoreSearchError(raw);
          const productInfo = getProductInfo({ isHomeDepot, searchResults: enrichedSearchResults, homeDepotSearchResults: enrichedHomeDepotResults, retailer });

          if (!raw.length) return null;
          if (!filtered.length) return (
            <div className="p-10 text-center">
              {resultError ? (
                <>
                  <AlertCircle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
                  <p className="text-sm text-amber-800 dark:text-amber-300 font-medium">{resultError}</p>
                </>
              ) : (
                <>
                  <Package className="w-12 h-12 text-gray-300 dark:text-dark-500 mx-auto mb-4" />
                  <p className="text-sm text-gray-500 dark:text-dark-300 font-medium">{t("products.noResultsForProduct")}</p>
                </>
              )}
            </div>
          );

          return (
            <NearbyStoreResultsList
              results={filtered}
              retailer={retailer}
              storeSort={storeSort}
              onSortChange={setStoreSort}
              productMsrp={productInfo?.msrp}
            />
          );
        })()}

        {/* Loading state */}
        {isSearching && (
          <div className="flex-1 bg-gray-50 px-4 py-8 dark:bg-dark-900 sm:px-6">
            <div className="mx-auto flex max-w-md flex-col items-center text-center">
              <h4 className="text-lg font-semibold text-gray-900 dark:text-dark-50">
                {t("products.scanningForUpdates")}
              </h4>
              <p className="mt-1 text-xs text-gray-500 dark:text-dark-300">
                {t("common.searchingMessage")}
              </p>

              {retailer === "walmart" && (
                <div className="mt-5 w-full overflow-hidden rounded-2xl border border-amber-200/70 bg-amber-50/80 text-left shadow-sm dark:border-amber-700/40 dark:bg-amber-950/20">
                  <div className="border-b border-amber-200/70 px-4 py-3 dark:border-amber-800/50">
                    <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-amber-700 dark:text-amber-300">
                      {t("products.nearbyTipsTitle")}
                    </p>
                  </div>
                  <div className="divide-y divide-amber-200/70 dark:divide-amber-800/50">
                    {loadingTips.map((tip, index) => (
                      <div key={tip} className="flex gap-3 px-4 py-3">
                        <span className="mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-white text-xs font-bold text-amber-700 shadow-sm ring-1 ring-amber-200 dark:bg-amber-900/50 dark:text-amber-200 dark:ring-amber-700/50">
                          {index + 1}
                        </span>
                        <p className="text-sm leading-5 text-amber-900 dark:text-amber-100">
                          {tip}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="mt-6 w-full rounded-3xl border border-gray-200 bg-white p-4 shadow-sm dark:border-dark-700 dark:bg-dark-800">
                <div className="space-y-4 animate-pulse">
                  <div className="flex items-center justify-between gap-3">
                    <div className="space-y-2">
                      <div className="h-3 w-20 rounded-full bg-gray-200 dark:bg-dark-700" />
                      <div className="h-4 w-32 rounded-full bg-gray-100 dark:bg-dark-600" />
                    </div>
                    <div className="h-8 w-16 rounded-xl bg-gray-100 dark:bg-dark-600" />
                  </div>
                  {[0, 1, 2].map((row) => (
                    <div key={row} className="flex items-start justify-between gap-3 border-t border-gray-100 pt-4 dark:border-dark-700">
                      <div className="flex-1 space-y-2">
                        <div className="h-3 w-24 rounded-full bg-gray-200 dark:bg-dark-700" />
                        <div className="h-3 w-full rounded-full bg-gray-100 dark:bg-dark-600" />
                        <div className="h-3 w-2/3 rounded-full bg-gray-100 dark:bg-dark-600" />
                      </div>
                      <div className="w-20 space-y-2">
                        <div className="ml-auto h-3 w-10 rounded-full bg-gray-100 dark:bg-dark-600" />
                        <div className="h-4 w-16 rounded-full bg-gray-200 dark:bg-dark-700 ml-auto" />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <DiscordPriceModal
        isOpen={showDiscordModal}
        onClose={() => setShowDiscordModal(false)}
        product={discordProduct}
        retailer={retailer}
        zipCode={zipForDistance}
      />

      {showImageViewer && (() => {
        const productInfo = getProductInfo({ isHomeDepot, searchResults: enrichedSearchResults, homeDepotSearchResults: enrichedHomeDepotResults, retailer });
        if (!productInfo?.image) return null;
        return (
          <div className="fixed inset-0 z-[999999] bg-black flex items-center justify-center" onClick={() => setShowImageViewer(false)}>
            <button onClick={() => setShowImageViewer(false)} className="absolute top-4 left-4 p-2 rounded-full bg-black/50 hover:bg-black/70 text-white transition-colors z-10">
              <X className="w-6 h-6" />
            </button>
            <img src={productInfo.image} alt={productInfo.name} className="max-w-[90vw] max-h-[90vh] object-contain" onClick={(e) => e.stopPropagation()} />
          </div>
        );
      })()}
    </div>,
    document.body
  );
};

export default NearbyResults;
